<script setup lang="ts">
import { onMounted, ref } from "vue";
import { isOaFriend, promptAddFriend } from "../liff.js";

/**
 * The add-friend gate. Not a route — main.ts mounts this *instead of* the app
 * when liff.getFriendship() says the caller hasn't added the BarkUp OA, and
 * only boots the real app once they have (see isOaFriend's doc comment for why
 * this is a precondition rather than an upsell).
 */
const emit = defineEmits<{ added: [] }>();

const busy = ref(false);
const declined = ref(false);
const unavailable = ref(false);

/**
 * `attempt` covers both the automatic prompt on arrival and the manual retry.
 * requestFriendship() never reports the user's choice, so the answer always
 * comes from re-asking LINE after the subwindow closes.
 */
async function attempt(manual: boolean) {
  if (busy.value) return;
  busy.value = true;
  declined.value = false;
  try {
    await promptAddFriend();
    unavailable.value = false;
  } catch {
    // Outside the LINE client, or the LIFF app isn't sized Full (a console
    // setting — see DEPLOYMENT.md §2.4). Nothing was asked, so the copy below
    // has to tell the user to add the OA by hand.
    unavailable.value = true;
    busy.value = false;
    return;
  }
  if (await isOaFriend()) emit("added");
  else if (manual) declined.value = true;
  busy.value = false;
}

// Ask straight away — one tap in the native subwindow is the shortest path in,
// and the copy below stays as the fallback if the user dismisses it.
onMounted(() => void attempt(false));
</script>

<template>
  <div class="flex min-h-screen flex-col items-center justify-center bg-surface px-container-margin text-center font-body-md text-on-surface">
    <div class="flex h-16 w-16 items-center justify-center rounded-full bg-primary-container">
      <span class="material-symbols-outlined text-[30px] text-primary">person_add</span>
    </div>

    <h1 class="mt-stack-md font-headline-lg text-[22px] font-bold tracking-tight text-primary">เพิ่ม BarkUp เป็นเพื่อนก่อน</h1>
    <p class="mt-stack-sm max-w-[300px] font-body-sm text-on-surface-variant">
      BarkUp ทำงานผ่านแชท LINE — ต้องเป็นเพื่อนกันก่อน บอทถึงจะเก็บไฟล์ที่คุณส่ง
      และส่งลิงก์คลังกลับหาคุณได้
    </p>

    <button
      type="button"
      :disabled="busy"
      class="mt-stack-lg flex h-12 min-w-[220px] items-center justify-center gap-2 rounded-full bg-primary px-6 font-body-md font-bold text-on-primary transition-colors active:bg-primary/90 disabled:opacity-50"
      @click="attempt(true)"
    >
      <span class="material-symbols-outlined text-[20px]">add</span>
      {{ busy ? "กำลังเปิด..." : "เพิ่มเพื่อน" }}
    </button>

    <p v-if="declined" class="mt-stack-sm max-w-[300px] font-metadata text-on-surface-variant">
      ยังไม่ได้เพิ่มเพื่อน — กดปุ่มด้านบนอีกครั้งเพื่อเข้าใช้งาน
    </p>
    <p v-if="unavailable" class="mt-stack-sm max-w-[300px] font-metadata text-on-surface-variant">
      เปิดหน้าต่างเพิ่มเพื่อนไม่ได้ กรุณาเพิ่ม BarkUp เป็นเพื่อนในแอป LINE
      แล้วเปิดแอปนี้ใหม่อีกครั้ง
    </p>
  </div>
</template>
